import { useEffect, useRef, useState } from 'react';
import type { ShiftEmployeeSchedulingDetail, ShiftEmployeeSchedulingDirectory, ShiftEmployeeSchedulingPatch, ShiftEmployeeLimitKey, ShiftEmployeeLimits, ShiftEmployeeMetadata, ShiftEmployeeMetadataKey } from 'shared-models';
import { getCurrentWeekStartIsoDate } from '../../utils/date.utils';
import { EMPLOYEE_LIMIT_KEYS } from '../../utils/employee-limits.utils';
import { useShiftEvents } from '../../contexts/ShiftEventsProvider';
import { shiftEmployeesService } from '../../services/employees/shift-employees.service';
import { applyEmployeeSchedulingEvent, applySchedulingDirectoryEvent, EMPLOYEE_METADATA_FIELDS } from './employee-scheduling.utils';
import type { EmployeeSchedulingEvent } from './employee-scheduling.utils';

export function useEmployeeSchedulingPageController() {
  const [directory, setDirectory] = useState<ShiftEmployeeSchedulingDirectory | null>(null);
  const [detail, setDetail] = useState<ShiftEmployeeSchedulingDetail | null>(null);
  const [employeePk, setEmployeePk] = useState<number | null>(null);
  const [offset, setOffset] = useState(0);
  const [roleOffset, setRoleOffset] = useState(0);
  const [weekStartDate, setWeekStartDate] = useState(getCurrentWeekStartIsoDate);
  const [limitsDraft, setLimitsDraft] = useState<{ values:Partial<Record<ShiftEmployeeLimitKey,string | null>>; expectedRevision:number } | null>(null);
  const [metadataDraft, setMetadataDraft] = useState<{values:Partial<Record<ShiftEmployeeMetadataKey,string>>;expectedRevision:number} | null>(null);
  const [reload, setReload] = useState(0);
  const [isLoadingDirectory, setIsLoadingDirectory] = useState(true);
  const [isLoadingDetail, setIsLoadingDetail] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isOutdated, setIsOutdated] = useState(false);
  const [directoryError, setDirectoryError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [priorityDraft, setPriorityDraft] = useState<{ value: string; expectedRevision: number } | null>(null);
  const [tipDraft, setTipDraft] = useState<{ includeInTips: boolean; personalMinimum: string | null; expectedRevision: number } | null>(null);
  const [roleDrafts, setRoleDrafts] = useState<Record<number, { isEligible: boolean; expectedRevision: number }>>({});
  const saveLock = useRef(false);
  const directoryOverlays = useRef(new Map<string, EmployeeSchedulingEvent>());
  const detailOverlays = useRef(new Map<string, EmployeeSchedulingEvent>());
  const latestLimits = useRef(new Map<number,ShiftEmployeeLimits>());
  const latestMetadata = useRef(new Map<number,ShiftEmployeeMetadata>());
  const keepLatestMetadata = (setting:ShiftEmployeeMetadata) => {
    if (setting.revision > (latestMetadata.current.get(setting.employeePk)?.revision ?? -1)) latestMetadata.current.set(setting.employeePk,setting);
    return latestMetadata.current.get(setting.employeePk)!;
  };
  const keepLatestLimits = (limits:ShiftEmployeeLimits) => {
    if (limits.revision > (latestLimits.current.get(limits.employeePk)?.revision ?? -1)) latestLimits.current.set(limits.employeePk,limits);
    return latestLimits.current.get(limits.employeePk)!;
  };
  const eventsReady = useShiftEvents(event => {
    if (event.type === 'employeeMetadata.upserted') {
      if ((latestMetadata.current.get(event.setting.employeePk)?.revision ?? -1) >= event.setting.revision) return;
      keepLatestMetadata(event.setting);
    }
    if (event.type === 'employeeLimits.upserted') {
      if ((latestLimits.current.get(event.setting.employeePk)?.revision ?? -1) >= event.setting.revision) return;
      keepLatestLimits(event.setting);
    }
    if (event.type === 'sse.resync') setReload(current => current + 1);
    else if (event.type === 'employeeScheduling.invalidated') setIsOutdated(true);
    else if (event.type === 'employeePriority.upserted' || event.type === 'employeeEligibility.upserted' || event.type === 'employeeTipSettings.upserted' || event.type === 'employeeLimits.upserted' || event.type === 'employeeMetadata.upserted') {
      if (event.type !== 'employeeEligibility.upserted' && event.type !== 'employeeMetadata.upserted') {
        const directoryKey = `${event.type}:${event.setting.employeePk}`;
        if ((directoryOverlays.current.get(directoryKey)?.setting.revision ?? 0) < event.setting.revision) {
          directoryOverlays.current.set(directoryKey, event);
          setDirectory(current => current ? applySchedulingDirectoryEvent(current, event) : current);
        }
      }
      if (event.setting.employeePk !== employeePk) return;
      const key = event.type === 'employeePriority.upserted' ? 'priority' : event.type === 'employeeTipSettings.upserted' ? 'tips' : event.type === 'employeeLimits.upserted' ? 'limits' : event.type === 'employeeMetadata.upserted' ? 'metadata' : `role:${event.setting.roleId}`;
      if ((detailOverlays.current.get(key)?.setting.revision ?? 0) >= event.setting.revision) return;
      detailOverlays.current.set(key, event);
      setDetail(current => current ? applyEmployeeSchedulingEvent(current, event) : current);
    }
  });

  useEffect(() => {
    if (!eventsReady) return;
    let active = true;
    directoryOverlays.current.clear();
    setDirectory(null);
    setIsLoadingDirectory(true);
    setDirectoryError(null);
    void Promise.resolve().then(async () => {
      if (!active) return;
      try {
        let snapshot = await shiftEmployeesService.listScheduling(offset);
        if (!active) return;
        for (const event of directoryOverlays.current.values()) snapshot = applySchedulingDirectoryEvent(snapshot, event);
        snapshot = {...snapshot,employees:snapshot.employees.map(employee => employee.limits ? {...employee,limits:keepLatestLimits(employee.limits)} : employee)};
        setDirectory(snapshot);
      } catch {
        if (active) setDirectoryError('לא ניתן לטעון את העובדים.');
      } finally {
        if (active) setIsLoadingDirectory(false);
      }
    });
    return () => { active = false; };
  }, [eventsReady, offset, reload]);

  useEffect(() => {
    if (!eventsReady || employeePk === null) return;
    let active = true;
    detailOverlays.current.clear();
    setDetail(current => current?.employee.employeePk === employeePk && current.pagination.offset === roleOffset ? current : null);
    setIsLoadingDetail(true);
    setError(null);
    setIsOutdated(false);
    void Promise.resolve().then(async () => {
      if (!active) return;
      try {
        let snapshot = await shiftEmployeesService.getScheduling(employeePk, roleOffset, weekStartDate);
        if (!active) return;
        for (const event of detailOverlays.current.values()) snapshot = applyEmployeeSchedulingEvent(snapshot, event);
        if (snapshot.employee.limits) snapshot = applyEmployeeSchedulingEvent(snapshot,{type:'employeeLimits.upserted',setting:keepLatestLimits(snapshot.employee.limits)});
        if (snapshot.metadata) keepLatestMetadata(snapshot.metadata);
        const metadata = latestMetadata.current.get(employeePk);
        if (metadata) snapshot = applyEmployeeSchedulingEvent(snapshot,{type:'employeeMetadata.upserted',setting:metadata});
        setDetail(snapshot);
      } catch {
        if (active) setError('לא ניתן לטעון את כללי השיבוץ של העובד.');
      } finally {
        if (active) setIsLoadingDetail(false);
      }
    });
    return () => { active = false; };
  }, [eventsReady, employeePk, roleOffset, reload, weekStartDate]);

  const hasChanges = priorityDraft !== null || tipDraft !== null || limitsDraft !== null || metadataDraft !== null || Object.keys(roleDrafts).length > 0;
  const isValid = (!priorityDraft || (priorityDraft.value !== '' && Number.isInteger(Number(priorityDraft.value)) && Number(priorityDraft.value) >= 0 && Number(priorityDraft.value) <= 100))
    && (!tipDraft || tipDraft.personalMinimum === null || (/^\d+$/.test(tipDraft.personalMinimum) && Number(tipDraft.personalMinimum) <= 2147483647))
    && (!limitsDraft || Object.values(limitsDraft.values).every(value => value === null || (/^\d+$/.test(value!) && Number(value) <= 1000)))
    && (!metadataDraft || EMPLOYEE_METADATA_FIELDS.every(field => (metadataDraft.values[field.key]?.trim().length ?? 0) <= field.maximum));
  const hasConflicts = !!priorityDraft && priorityDraft.expectedRevision !== detail?.employee.priorityRevision
    || !!tipDraft && tipDraft.expectedRevision !== (detail?.employee.tipSettings?.revision ?? 0)
    || !!limitsDraft && limitsDraft.expectedRevision !== (detail?.employee.limits?.revision ?? 0)
    || !!metadataDraft && metadataDraft.expectedRevision !== (detail?.metadata?.revision ?? 0)
    || Object.entries(roleDrafts).some(([roleId, draft]) => detail?.roles.find(role => role.roleId === Number(roleId))?.revision !== draft.expectedRevision);
  const navigationDisabled = hasChanges || isSaving || isLoadingDirectory || isLoadingDetail;

  const onChangePriority = (value: string) => {
    if (!detail?.canEdit || isLoadingDetail || saveLock.current) return;
    setPriorityDraft(current => value !== '' && Number(value) === detail.employee.priority ? null
      : { value, expectedRevision: current?.expectedRevision ?? detail.employee.priorityRevision });
  };
  const onChangeEligibility = (roleId: number, isEligible: boolean) => {
    const role = detail?.roles.find(current => current.roleId === roleId);
    if (!role || !detail?.canEdit || isLoadingDetail || saveLock.current) return;
    setRoleDrafts(current => {
      const next = { ...current };
      if (role.isEligible === isEligible) delete next[roleId];
      else next[roleId] = { isEligible, expectedRevision: current[roleId]?.expectedRevision ?? role.revision };
      return next;
    });
  };
  const onChangeTips = (change: Partial<{ includeInTips: boolean; personalMinimum: string | null }>) => {
    if (!detail?.canEdit || isLoadingDetail || saveLock.current) return;
    const saved = detail.employee.tipSettings;
    const baseline = { includeInTips: saved?.includeInTips ?? true,
      personalMinimum: saved?.personalMinimum == null ? null : String(saved.personalMinimum), expectedRevision: saved?.revision ?? 0 };
    setTipDraft(current => {
      const next = { ...(current ?? baseline), ...change };
      return next.includeInTips === baseline.includeInTips && next.personalMinimum === baseline.personalMinimum ? null : next;
    });
  };
  const getLimitValue = (key:ShiftEmployeeLimitKey):string | null => limitsDraft && key in limitsDraft.values
    ? limitsDraft.values[key]! : detail?.employee.limits?.[key] == null ? null : String(detail.employee.limits[key]);
  const onChangeLimit = (key:ShiftEmployeeLimitKey,value:string | null) => {
    if (!detail?.canEdit || isLoadingDetail || saveLock.current) return;
    setLimitsDraft(current => {
      const values = { ...current?.values, [key]:value };
      const baseline = detail.employee.limits?.[key] ?? null;
      if (value === null ? baseline === null : value !== '' && Number(value) === baseline) delete values[key];
      return Object.keys(values).length ? {values,expectedRevision:current?.expectedRevision ?? detail.employee.limits?.revision ?? 0} : null;
    });
  };
  const getMetadataValue = (key:ShiftEmployeeMetadataKey) => metadataDraft?.values[key] ?? detail?.metadata?.[key] ?? '';
  const onChangeMetadata = (key:ShiftEmployeeMetadataKey,value:string) => {
    if (!detail?.canEdit || isLoadingDetail || saveLock.current) return;
    setMetadataDraft(current => {
      const values = {...current?.values,[key]:value};
      if ((value.trim() || null) === (detail.metadata?.[key] ?? null)) delete values[key];
      return Object.keys(values).length ? {values,expectedRevision:current?.expectedRevision ?? detail.metadata?.revision ?? 0} : null;
    });
  };
  const onDiscardChanges = () => { setPriorityDraft(null); setTipDraft(null); setLimitsDraft(null); setMetadataDraft(null); setRoleDrafts({}); setError(null); };
  const onSaveSettings = async () => {
    if (saveLock.current || !detail?.canEdit || isLoadingDetail || !hasChanges || !isValid || hasConflicts || isOutdated) return;
    saveLock.current = true;
    setIsSaving(true);
    setError(null);
    const patch: ShiftEmployeeSchedulingPatch = {};
    if (metadataDraft) {
      patch.metadata = {expectedRevision:metadataDraft.expectedRevision};
      for (const {key} of EMPLOYEE_METADATA_FIELDS) if (key in metadataDraft.values)
        patch.metadata[key] = metadataDraft.values[key]?.trim() || null;
    }
    if (priorityDraft) patch.priority = { value: Number(priorityDraft.value), expectedRevision: priorityDraft.expectedRevision };
    if (tipDraft) {
      patch.tips = { expectedRevision: tipDraft.expectedRevision };
      if (tipDraft.includeInTips !== (detail.employee.tipSettings?.includeInTips ?? true)) patch.tips.includeInTips = tipDraft.includeInTips;
      const minimum = tipDraft.personalMinimum === null ? null : Number(tipDraft.personalMinimum);
      if (minimum !== (detail.employee.tipSettings?.personalMinimum ?? null)) patch.tips.personalMinimum = minimum;
    }
    if (Object.keys(roleDrafts).length) patch.roles = Object.entries(roleDrafts).map(([roleId, draft]) => ({ roleId: Number(roleId), ...draft }));
    if (limitsDraft) {
      patch.limits = {expectedRevision:limitsDraft.expectedRevision};
      for (const key of EMPLOYEE_LIMIT_KEYS) if (key in limitsDraft.values) {
        const value = limitsDraft.values[key] === null ? null : Number(limitsDraft.values[key]);
        if (value !== (detail.employee.limits?.[key] ?? null)) patch.limits[key] = value;
      }
    }
    try {
      await shiftEmployeesService.patchScheduling(detail.employee.employeePk, patch);
      onDiscardChanges();
    } catch {
      setError('לא ניתן לשמור את כללי השיבוץ. השינויים נשמרו במסך.');
    } finally {
      saveLock.current = false;
      setIsSaving(false);
    }
  };
  return {
    directory, detail, employeePk, offset, roleOffset, isLoadingDirectory, isLoadingDetail, isSaving, isOutdated, directoryError, error,
    priorityDraft, tipDraft, roleDrafts, hasChanges, isValid, hasConflicts, navigationDisabled,
    weekStartDate,getLimitValue,onChangeLimit,
    getMetadataValue,onChangeMetadata,
    onChangeWeek:(value:string) => { if (value && !navigationDisabled && !saveLock.current) setWeekStartDate(value); },
    onChangePriority, onChangeTips, onChangeEligibility, onDiscardChanges, onSaveSettings,
    onSelectEmployee: (id: number) => { if (!navigationDisabled && !saveLock.current) { setEmployeePk(id); setRoleOffset(0); } },
    onChangeEmployeePage: (value: number) => { if (!navigationDisabled && !saveLock.current) { setOffset(value); setEmployeePk(null); setDetail(null); setRoleOffset(0); } },
    onChangeRolePage: (value: number) => { if (!navigationDisabled && !saveLock.current) setRoleOffset(value); },
    onRefresh: () => { if (!hasChanges && !saveLock.current) setReload(current => current + 1); },
  };
}